<div class="wizard">
  <div class="wizard__head">
    <mat-icon>medical_services</mat-icon>
    <h2>{{ record ? ('Folio ' + record.fileNumber + (record.status === 'Borrador' || record.status === 'Incompleto' ? ' (incompleto)' : '')) : 'Nuevo folio de salida de material' }}</h2>
    <span class="spacer"></span>
    <button mat-stroked-button (click)="saveDraftAndExit()"><mat-icon>save</mat-icon>Guardar borrador y salir</button>
    <button mat-stroked-button color="warn" (click)="closeWithoutSaving()"><mat-icon>close</mat-icon>Cerrar sin guardar</button>
  </div>

  <!-- ── Barra de pasos ─────────────────────────────────────────────── -->
  <div class="steps">
    @for (title of stepTitles; track $index) {
      <button class="steps__item"
              [class.steps__item--active]="step === $index"
              [class.steps__item--done]="step > $index"
              (click)="goToStep($index)">
        <span class="steps__number">{{ $index + 1 }}</span>
        <span class="steps__label">{{ title }}</span>
      </button>
      @if ($index < stepTitles.length - 1) { <span class="steps__sep"></span> }
    }
  </div>

  <!-- ═══ Paso 1: Técnico, hospital, médico, folio y servicio ═══════════════════════════ -->
  @if (step === 0) {
    <form [formGroup]="generalForm" class="panel">
      <h3>Técnico Medtronic</h3>
      @if (technicianLocked) {
        <p class="hint">Capturas con tu usuario de técnico: <b>{{ technicianLockedName }}</b></p>
      } @else if (technicianList.length === 0) {
        <p class="hint hint--warn">No hay usuarios con rol <b>Técnico</b>. Da de alta al técnico en Usuarios (o revisa su rol) para poder generar el folio.</p>
      } @else {
        <mat-radio-group formControlName="technician" class="radio-list">
          @for (tech of technicianList; track tech.id) {
            <mat-radio-button [value]="tech.id">{{ tech.name }}</mat-radio-button>
          }
        </mat-radio-group>
      }

      <!-- El hospital va antes que el médico: la lista de médicos se filtra
           por los que operan en el hospital elegido. -->
      <h3>Hospital</h3>
      <div class="grid">
        <app-auto-select label="Hospital" formControlName="hospital" [options]="scopedPlaces" [labelFn]="placeAliasLabel" (changed)="onHospitalChange()"></app-auto-select>
      </div>

      <h3>Médico</h3>
      <div class="grid">
        <app-auto-select label="Médico" formControlName="doctor" [options]="doctorsOfHospital" [labelFn]="doctorLabel"></app-auto-select>
        <button mat-stroked-button type="button" class="inline-add" (click)="creatingDoctor = !creatingDoctor">
          <mat-icon>person_add</mat-icon>{{ creatingDoctor ? 'Cancelar' : 'Otro (nuevo médico)' }}
        </button>
      </div>
      @if (creatingDoctor) {
        <div class="inline-form">
          <mat-form-field><mat-label>Nombre del médico</mat-label><input matInput [(ngModel)]="newDoctor.name" [ngModelOptions]="{standalone: true}"></mat-form-field>
          <!-- El hospital es forzosamente el del paso 1 (no se puede cambiar). -->
          <mat-form-field><mat-label>Hospital</mat-label>
            <input matInput [value]="selectedHospitalLabel || 'Elige primero el hospital'" disabled>
            <mat-icon matSuffix>lock</mat-icon>
          </mat-form-field>
          <button mat-raised-button color="accent" type="button" [disabled]="!generalForm.get('hospital')?.value" (click)="createDoctor()">Agregar médico</button>
          <button mat-button type="button" (click)="creatingDoctor = false"><mat-icon>close</mat-icon>Cerrar</button>
        </div>
        @if (similarDoctors.length > 0) {
          <div class="similar-doctors">
            <p class="hint hint--warn"><mat-icon inline>info</mat-icon> ¿No es alguno de estos? Ya están dados de alta:</p>
            @for (d of similarDoctors; track d.id) {
              <div class="similar-doctors__row">
                <span><b>{{ doctorLabel(d) }}</b> · {{ doctorHospitalsLabel(d) }}</span>
                <button mat-stroked-button type="button" color="primary" (click)="useExistingDoctor(d)">
                  {{ doctorInHospital(d) ? 'Usar este médico' : 'Usar y agregar a este hospital' }}
                </button>
              </div>
            }
          </div>
        }
      }

      <!-- Preferencia del médico: vista previa por Cx (hospital + médico) -->
      @if (generalForm.get('doctor')?.value && generalForm.get('hospital')?.value) {
        <div class="pref">
          <div class="pref__head">
            <mat-icon>medical_information</mat-icon>
            <div>
              <b>Preferencia del médico</b>
              @if (preference) { <span> · calculada de {{ preference.folios }} folio{{ preference.folios === 1 ? '' : 's' }} en {{ preference.hospital }}</span> }
            </div>
            <span class="spacer"></span>
            <button mat-stroked-button type="button" [disabled]="!preferenceSelected" (click)="downloadPreference()">
              <mat-icon>picture_as_pdf</mat-icon>Descargar esta Cx
            </button>
          </div>
          @if (preferenceLoading) {
            <p class="hint">Calculando la preferencia…</p>
          } @else if (!preference?.surgeries?.length) {
            <p class="hint">Este médico todavía no tiene folios con insumos en este hospital.</p>
          } @else {
            <div class="pref__tabs" role="tablist">
              @for (sg of preference!.surgeries; track $index) {
                <button type="button" role="tab" class="pref__tab" [class.pref__tab--on]="preferenceSurgery === $index"
                        [attr.aria-selected]="preferenceSurgery === $index" (click)="preferenceSurgery = $index">
                  {{ sg.surgery }} <small>{{ sg.folios }}</small>
                </button>
              }
            </div>
            @if (preferenceSelected; as sg) {
              <div class="pref__table">
                <table>
                  <thead><tr><th>Código</th><th>Descripción</th><th class="r">Cant.</th><th class="r">Uso</th></tr></thead>
                  <tbody>
                    @for (it of sg.items; track it.code) {
                      <tr><td><code>{{ it.code }}</code></td><td>{{ it.description }}</td><td class="r b">{{ it.quantity }}</td><td class="r">{{ it.share }}%</td></tr>
                    } @empty {
                      <tr><td colspan="4" class="hint">Sin códigos frecuentes en esta Cx.</td></tr>
                    }
                  </tbody>
                </table>
              </div>
              <p class="hint">{{ sg.folios }} folio{{ sg.folios === 1 ? '' : 's' }} · último {{ sg.lastDate }}{{ sg.omitted ? ' · +' + sg.omitted + ' códigos poco usados' : '' }}</p>
            }
          }
        </div>
      }

      <h3>Folio y servicio</h3>
      <div class="grid">
        <mat-form-field><mat-label>Fecha de cirugía</mat-label>
          <input matInput [matDatepicker]="folioDatePicker" [(ngModel)]="interventionDate" [ngModelOptions]="{standalone: true}" (dateChange)="updateInterventionDatetime()">
          <mat-datepicker-toggle matSuffix [for]="folioDatePicker"></mat-datepicker-toggle>
          <mat-datepicker #folioDatePicker></mat-datepicker>
        </mat-form-field>
        <!-- Rueda 24 h: columnas 00-23 : 00-59, sin AM/PM -->
        <app-time-wheel class="narrow" label="Hora" [(ngModel)]="interventionTimeStr" [ngModelOptions]="{standalone: true}" (ngModelChange)="updateInterventionDatetime()"></app-time-wheel>
        <div class="radio-inline">
          <span class="radio-inline__label">Tipo</span>
          <mat-radio-group formControlName="type">
            <mat-radio-button value="Programada">Programada</mat-radio-button>
            <mat-radio-button value="Urgencia en turno">Urgencia en turno</mat-radio-button>
            <mat-radio-button value="Urgencia fuera de turno">Urgencia fuera de turno</mat-radio-button>
          </mat-radio-group>
        </div>
        <app-auto-select label="Servicio hospitalario (código MDT)" formControlName="service" [options]="hospitalServicesList" [labelFn]="serviceLabel" [allowEmpty]="true" emptyLabel="— Sin servicio —" (changed)="onServiceChange()"></app-auto-select>
      </div>

      <div class="folio-box">
        <mat-icon>tag</mat-icon>
        <mat-form-field class="folio-field" subscriptSizing="dynamic">
          <mat-label>Folio</mat-label>
          <input matInput formControlName="fileNumber" placeholder="Escribe el folio" autocomplete="off" (input)="computeFolioPreview()">
        </mat-form-field>
      </div>

      <div class="actions">
        <button mat-raised-button color="accent" type="button"
                [disabled]="!generalForm.get('technician')?.value || !generalForm.get('hospital')?.value || !generalForm.get('doctor')?.value || !generalForm.get('interventionDatetime')?.value"
                (click)="step = 1">Continuar<mat-icon iconPositionEnd>arrow_forward</mat-icon></button>
      </div>
    </form>
  }

  <!-- ═══ Paso 2: Cirugía y equipo ═════════════════════════════════════ -->
  @if (step === 1) {
    <form [formGroup]="generalForm" class="panel">
      <h3>Cirugía</h3>
      <div class="grid">
        <mat-form-field><mat-label>CIR</mat-label><input matInput formControlName="cir"></mat-form-field>
        <mat-form-field><mat-label>Episodio</mat-label><input matInput formControlName="episode"></mat-form-field>
        <!-- Rueda 24 h: columnas 00-23 : 00-59, sin AM/PM -->
        <app-time-wheel class="narrow" label="Inicio de Cx (24 hrs)" formControlName="startHour"></app-time-wheel>
        <app-time-wheel class="narrow" label="Fin de Cx (24 hrs)" formControlName="finishHour"></app-time-wheel>
        @if (hoursInvalid) {<p class="hint hint--warn hours-error">El fin de Cx no puede ser anterior o igual al inicio.</p>}
        <mat-form-field><mat-label>Quirófano (sala)</mat-label><input matInput formControlName="operationRoom" autocomplete="off"></mat-form-field>
        <app-auto-select label="Cx (cirugía)" formControlName="procedure" [options]="proceduresOfSpecialty" (changed)="onProcedureChange()"></app-auto-select>
        <app-auto-select label="Especialidad" formControlName="specialty" [options]="scopedSpecialties"></app-auto-select>
        <button mat-stroked-button type="button" class="inline-add" (click)="toggleNewProcedure()">
          <mat-icon>add</mat-icon>{{ creatingProcedure ? 'Cancelar' : 'Otra (nueva Cx)' }}
        </button>
      </div>
      @if (creatingProcedure) {
        <div class="inline-form">
          <mat-form-field><mat-label>Nombre de la Cx</mat-label><input matInput [(ngModel)]="newProcedure.name" [ngModelOptions]="{standalone: true}" autocomplete="off"></mat-form-field>
          @if (newProcedureSpecialtyLocked) {
            <mat-form-field><mat-label>Especialidad</mat-label>
              <input matInput [value]="newProcedureSpecialtyLabel" disabled>
              <mat-icon matSuffix>lock</mat-icon>
            </mat-form-field>
          } @else {
            <app-auto-select label="Especialidad" [(ngModel)]="newProcedure.specialty" [ngModelOptions]="{standalone: true}" [options]="specialtyList"></app-auto-select>
          }
          <button mat-raised-button color="accent" type="button" (click)="createProcedure()">Agregar Cx</button>
          <button mat-button type="button" (click)="creatingProcedure = false"><mat-icon>close</mat-icon>Cerrar</button>
        </div>
        @if (similarProcedures.length > 0) {
          <div class="similar-doctors">
            <p class="hint hint--warn"><mat-icon inline>info</mat-icon> ¿Quisiste decir? Estas Cx ya existen:</p>
            @for (p of similarProcedures; track p.id) {
              <div class="similar-doctors__row">
                <span><b>{{ p.name }}</b> · {{ specialtyName(p.specialty) }}</span>
                <button mat-stroked-button type="button" color="primary" (click)="useExistingProcedure(p)">Usar esta Cx</button>
              </div>
            }
          </div>
        }
      }

      <h3>Paciente</h3>
      <div class="grid">
        @if (generalForm.get('patient')?.value) {
          <!-- El paciente ya capturado: solo se lee (no se sobrescribe). -->
          <mat-form-field><mat-label>Paciente</mat-label>
            <input matInput [value]="patientName(generalForm.get('patient')?.value)" readonly>
            <mat-icon matSuffix>lock</mat-icon>
          </mat-form-field>
        }
        <button mat-stroked-button type="button" class="inline-add" (click)="creatingPatient = !creatingPatient">
          <mat-icon>person_add</mat-icon>{{ creatingPatient ? 'Cancelar' : (generalForm.get('patient')?.value ? 'Otro paciente' : 'Nuevo paciente') }}
        </button>
      </div>
      @if (creatingPatient) {
        <div class="inline-form">
          <mat-form-field><mat-label>Nombre del paciente</mat-label><input matInput [(ngModel)]="newPatient.name" [ngModelOptions]="{standalone: true}" autocomplete="off"></mat-form-field>
          <button mat-raised-button color="accent" type="button" (click)="createPatient()">Agregar paciente</button>
          <button mat-button type="button" (click)="creatingPatient = false"><mat-icon>close</mat-icon>Cerrar</button>
        </div>
      }

      <h3>Equipo de cirugía</h3>
      <div class="grid">
        <app-auto-select label="Charola" [(ngModel)]="equipo.tray" [ngModelOptions]="{standalone: true}" [options]="trayOptions" [allowEmpty]="true"></app-auto-select>
        @if (equipo.tray === 'OTRA') {
          <mat-form-field><mat-label>¿Cuál charola?</mat-label><input matInput [(ngModel)]="equipo.trayOther" [ngModelOptions]="{standalone: true}"></mat-form-field>
        }
        <mat-form-field><mat-label>Número o color de charola</mat-label><input matInput [(ngModel)]="equipo.trayDetail" [ngModelOptions]="{standalone: true}"></mat-form-field>
        <mat-form-field><mat-label>Cabezal (no. de serie)</mat-label><input matInput [(ngModel)]="equipo.cabezal" [ngModelOptions]="{standalone: true}"></mat-form-field>
      </div>
      <div class="grid">
        <mat-form-field><mat-label>Lente 1 (serie)</mat-label><input matInput [(ngModel)]="equipo.lente1" [ngModelOptions]="{standalone: true}"></mat-form-field>
        <mat-form-field><mat-label>Lente 2 (serie)</mat-label><input matInput [(ngModel)]="equipo.lente2" [ngModelOptions]="{standalone: true}"></mat-form-field>
        <mat-form-field><mat-label>Lente 3 (serie)</mat-label><input matInput [(ngModel)]="equipo.lente3" [ngModelOptions]="{standalone: true}"></mat-form-field>
        <app-auto-select label="Torre (equipos del hospital)" [(ngModel)]="equipo.torre" [ngModelOptions]="{standalone: true}" [options]="towerOptions" [allowEmpty]="true" emptyLabel="— Sin torre —"></app-auto-select>
      </div>
      <div class="grid">
        <!-- SONICISION y energía de otra casa comercial son excluyentes:
             en una Cx se usa uno u otro, así que cada toggle oculta al otro. -->
        @if (!equipo.otherEnergyUsed) {
          <mat-slide-toggle [(ngModel)]="equipo.sonicisionUsed" [ngModelOptions]="{standalone: true}" (ngModelChange)="onSonicisionChange($event)">¿Se usó SONICISION?</mat-slide-toggle>
        }
        @if (!equipo.sonicisionUsed) {
          <mat-slide-toggle [(ngModel)]="equipo.otherEnergyUsed" [ngModelOptions]="{standalone: true}" (ngModelChange)="onOtherEnergyChange($event)">¿Se usó energía de otra casa comercial en esta Cx?</mat-slide-toggle>
        }
      </div>
      @if (equipo.sonicisionUsed) {
        <!-- Las especificaciones aparecen justo debajo del toggle -->
        <div class="grid">
          <mat-form-field><mat-label>Generador 1 (núm. de serie)</mat-label><input matInput [(ngModel)]="equipo.generator1" [ngModelOptions]="{standalone: true}"></mat-form-field>
          <mat-form-field><mat-label>Generador 2 (núm. de serie)</mat-label><input matInput [(ngModel)]="equipo.generator2" [ngModelOptions]="{standalone: true}"></mat-form-field>
          <mat-form-field><mat-label>Batería 1</mat-label><input matInput [(ngModel)]="equipo.battery1" [ngModelOptions]="{standalone: true}"></mat-form-field>
          <mat-form-field><mat-label>Batería 2</mat-label><input matInput [(ngModel)]="equipo.battery2" [ngModelOptions]="{standalone: true}"></mat-form-field>
        </div>
      }
      @if (equipo.otherEnergyUsed) {
        <div class="grid">
          <app-auto-select class="wide" label="¿De qué casa comercial? (Fabricantes)" [(ngModel)]="equipo.otherEnergyDetail" [ngModelOptions]="{standalone: true}" [options]="otherEnergyOptions"></app-auto-select>
        </div>
      }
      <mat-form-field class="wide"><mat-label>Comentarios adicionales</mat-label><input matInput formControlName="comments"></mat-form-field>

      <div class="actions">
        <button mat-button type="button" (click)="step = 0"><mat-icon>arrow_back</mat-icon>Atrás</button>
        <button mat-raised-button color="accent" type="button" (click)="saveStep1(true)">Guardar y continuar<mat-icon iconPositionEnd>arrow_forward</mat-icon></button>
      </div>
    </form>
  }

  <!-- ═══ Paso 3: Material (lectura RFID) ══════════════════════════════ -->
  @if (step === 2) {
    <div class="panel">
      @if (!record) {
        <p class="hint hint--warn">Todavía no guardas los datos (pasos 1-3): el borrador no existe y no se puede apartar material. Completa el paso 3 y presiona "Guardar y continuar".</p>
      }
      <p class="hint">Cada pieza que agregues queda <b>apartada</b> (Transito) para este folio; si la quitas, regresa a Disponible.</p>

      <div class="grid">
        <app-auto-select label="Locker del hospital" [(ngModel)]="selectedWarehouse" [ngModelOptions]="{standalone: true}" [options]="warehouseOptions" [disabled]="pieces.length > 0"></app-auto-select>
        <mat-button-toggle-group [(ngModel)]="rfidMode" (change)="onRfidModeChange()" class="mode-toggle">
          <mat-button-toggle value="widget"><mat-icon>qr_code_scanner</mat-icon> Widget</mat-button-toggle>
          <mat-button-toggle value="bandeja"><mat-icon>inbox</mat-icon> Bandeja</mat-button-toggle>
          <mat-button-toggle value="anaquel"><mat-icon>shelves</mat-icon> Anaquel</mat-button-toggle>
          <mat-button-toggle value="pistola"><mat-icon>settings_remote</mat-icon> Pistola</mat-button-toggle>
        </mat-button-toggle-group>
      </div>

      @if (rfidMode === 'widget') {
        <!-- Lectura uno a uno: el lector escribe la etiqueta y da Enter. -->
        <div class="grid">
          <mat-form-field class="wide"><mat-label>Escanear etiqueta RFID (24 caracteres)</mat-label>
            <input matInput maxlength="24" [(ngModel)]="scanValue" (keyup.enter)="scanTag()" [disabled]="!selectedWarehouse || !record" placeholder="El lector escribe aquí y da Enter">
            <mat-icon matSuffix>qr_code_scanner</mat-icon>
            <mat-hint align="end">{{ (scanValue || '').length }}/24</mat-hint>
          </mat-form-field>
        </div>

        <details #rowAdd1 class="row-add">
          <summary>Agregar material sin etiqueta (por renglón)</summary>
          <div class="grid">
            <app-auto-select label="Insumo (código o descripción)" [(ngModel)]="rowAdd.product" [options]="productsList" [labelFn]="productLabel"></app-auto-select>
            <mat-form-field><mat-label>Lote (opcional)</mat-label><input matInput [(ngModel)]="rowAdd.batch"></mat-form-field>
            <mat-form-field class="narrow"><mat-label>Cantidad</mat-label><input matInput type="number" min="1" step="1" (change)="rowAdd.quantity = toInt(rowAdd.quantity, 1)" [(ngModel)]="rowAdd.quantity"></mat-form-field>
            <button mat-stroked-button [disabled]="!selectedWarehouse || !record" (click)="addByRow()"><mat-icon>add</mat-icon>Apartar</button>
          </div>
                <div class="row-add__close">
          <button mat-button type="button" (click)="rowAdd1.open = false"><mat-icon>close</mat-icon>Cerrar</button>
        </div>
      </details>
      } @else {
        <!-- Lectura por dispositivo Jetstream: se lee, se revisa QUÉ leyó y se
             seleccionan las piezas con checkbox antes de apartarlas. -->
        <div class="grid">
          <mat-form-field><mat-label>{{ rfidMode === 'pistola' ? 'Dispositivo handheld (p.ej. Handheld-ICU)' : 'Nombre del dispositivo lector' }}</mat-label>
            <input matInput [(ngModel)]="deviceName" [disabled]="reading">
          </mat-form-field>
          <button mat-raised-button color="primary" [disabled]="reading || !selectedWarehouse || !record" (click)="readDevice()">
            <mat-icon>sensors</mat-icon>{{ reading ? 'Leyendo...' : 'Leer ' + rfidMode }}
          </button>
        </div>

        @if (readTags.length > 0) {
          <div class="read-actions">
            <span><b>{{ readTags.length }}</b> etiquetas leídas · {{ selectableTags }} disponibles para cargar</span>
            <span class="spacer"></span>
            <button mat-button (click)="toggleAllTags(true)">Seleccionar todos</button>
            <button mat-button (click)="toggleAllTags(false)">Deseleccionar todos</button>
            <button mat-raised-button color="accent" (click)="addSelectedTags()"><mat-icon>playlist_add_check</mat-icon>Apartar seleccionadas</button>
          </div>
          <table class="list">
            <tr><th></th><th>Etiqueta (EPC)</th><th>Código</th><th>Descripción</th><th>Lote</th><th>Estado</th></tr>
            @for (tag of readTags; track tag.epc) {
              <tr>
                <td>
                  <mat-checkbox [(ngModel)]="tag.selected" [disabled]="!tag.stockItem || tag.already"></mat-checkbox>
                </td>
                <td>{{ tag.epc }}</td>
                <td>{{ tag.stockItem?.code ?? '—' }}</td>
                <td>{{ tag.stockItem ? (productsObject[tag.stockItem.product]?.description ?? '—') : 'No está en el inventario de este locker' }}</td>
                <td>{{ tag.stockItem?.batch ?? '—' }}</td>
                <td>{{ tag.already ? 'Ya cargada' : (tag.stockItem?.status ?? '—') }}</td>
              </tr>
            }
          </table>
        }
      }

      <table class="list">
        <tr><th>Código</th><th>Descripción</th><th>Lote</th><th>Caducidad</th><th>Etiqueta</th><th>CIR</th><th></th></tr>
        @for (piece of pieces; track piece.id) {
          <tr>
            <td>{{ piece.code }}</td><td>{{ piece.description }}</td><td>{{ piece.batch }}</td>
            <td>{{ piece.expiration }}</td><td>{{ piece.tagRfid || 'Sin etiqueta' }}</td>
            <td>@switch (inCir(piece)) { @case (true) {<span class="cir-chip cir-chip--in">En CIR</span>} @case (false) {<span class="cir-chip cir-chip--out" matTooltip="No está en los insumos del CIR elegido: va al vale como fuera de CIR">Fuera de CIR</span>} @default {<span class="muted">—</span>} }</td>
            <td><button mat-icon-button color="warn" matTooltip="Quitar (regresa a Disponible)" (click)="removePiece(piece)"><mat-icon>close</mat-icon></button></td>
          </tr>
        }
      </table>
      @if (pieces.length === 0) { <p class="empty">Sin material cargado.</p> }
      <p class="count"><b>{{ pieces.length }}</b> piezas apartadas</p>

      <details #rowAdd2 class="row-add" [attr.open]="otherItems.length > 0 ? '' : null">
        <summary>Otros — insumos fuera de inventario (CFN / piezas / lote)</summary>
        <div class="grid">
          <mat-form-field><mat-label>CFN</mat-label><input matInput [(ngModel)]="otherAdd.cfn"></mat-form-field>
          <mat-form-field class="narrow"><mat-label>Piezas</mat-label><input matInput type="number" min="1" step="1" (change)="otherAdd.pieces = toInt(otherAdd.pieces, 1)" [(ngModel)]="otherAdd.pieces"></mat-form-field>
          <mat-form-field><mat-label>Lote</mat-label><input matInput [(ngModel)]="otherAdd.batch"></mat-form-field>
          <button mat-stroked-button [disabled]="!record" (click)="addOther()"><mat-icon>add</mat-icon>Agregar</button>
        </div>
        @if (otherItems.length > 0) {
          <table class="list">
            <tr><th>CFN</th><th>Piezas</th><th>Lote</th><th></th></tr>
            @for (other of otherItems; track $index) {
              <tr>
                <td>{{ other.cfn }}</td><td>{{ other.pieces }}</td><td>{{ other.batch || '—' }}</td>
                <td><button mat-icon-button color="warn" (click)="removeOther($index)"><mat-icon>close</mat-icon></button></td>
              </tr>
            }
          </table>
        }
              <div class="row-add__close">
          <button mat-button type="button" (click)="rowAdd2.open = false"><mat-icon>close</mat-icon>Cerrar</button>
        </div>
      </details>

      <div class="actions">
        <button mat-button (click)="step = 1"><mat-icon>arrow_back</mat-icon>Atrás</button>
        <button mat-raised-button color="accent" (click)="saveStep2(true)">Continuar<mat-icon iconPositionEnd>arrow_forward</mat-icon></button>
      </div>
    </div>
  }

  <!-- ═══ Paso 4: CIR (paquete) ══════════════════════════════════════════════ -->
  @if (step === 3) {
    <div class="panel">
      <p class="hint">Aparece primero (y ya elegido) el <b>CIR del servicio hospitalario</b> que se seleccionó en los datos generales: las {{ pieces.length }} piezas cargadas se comparan contra sus insumos. Lo que quede fuera del CIR se documenta en el vale (la remisión se captura aparte).</p>

      @if (suggestions.length === 0) {
        <p class="empty">El contrato no tiene CIRs (paquetes de servicio). Puedes continuar sin CIR (todo iría a remisión) o capturarlos en Contratos → Paquetes de servicio.</p>
      }

      <div class="packages">
        @for (sug of suggestions; track sug.pkg.id) {
          <div class="pkg-card" [class.pkg-card--selected]="selectedPackageId === sug.pkg.id" (click)="selectPackage(sug.pkg.id)">
            <div class="pkg-card__cover">{{ sug.pct }}%</div>
            <div class="pkg-card__body">
              <b>{{ sug.pkg.name }}</b>
              <span>{{ sug.covered }} de {{ pieces.length }} piezas cubiertas · {{ sug.totalItems }} partidas</span>
              @if (sug.pkg.fromService) { <span class="pkg-card__best">CIR seleccionado</span> } @else if (sug.covered > 0 && sug.covered === bestContractCoverage) { <span class="pkg-card__best">Mejor cobertura</span> }
            </div>
          </div>
        }
        <div class="pkg-card" [class.pkg-card--selected]="selectedPackageId === null" (click)="selectPackage(null)">
          <div class="pkg-card__cover">—</div>
          <div class="pkg-card__body"><b>Sin CIR</b><span>Todo el material se documenta en la remisión</span></div>
        </div>
      </div>

      @if (selectedPackageId !== null || pieces.length > 0) {
        <div class="split">
          <div><h4>Cubiertas por el CIR ({{ coveredPieces.length }})</h4>
            @for (piece of coveredPieces; track piece.id) { <p class="mini">{{ piece.code }} · {{ piece.batch }} · {{ piece.tagRfid || 'sin etiqueta' }}</p> }
          </div>
          <div><h4>Fuera de CIR ({{ outPieces.length }})</h4>
            @for (piece of outPieces; track piece.id) { <p class="mini mini--out">{{ piece.code }} · {{ piece.batch }} · {{ piece.tagRfid || 'sin etiqueta' }}</p> }
          </div>
        </div>
      }
      @if (outPieces.length > 0) {
        <mat-form-field class="wide">
          <mat-label>Fuera de CIR: ¿quién lo pidió o por qué se agregó?</mat-label>
          <input matInput [(ngModel)]="outOfCirReason" maxlength="1000" required autocomplete="off">
          <mat-hint>Obligatorio cuando hay material fuera del CIR.</mat-hint>
        </mat-form-field>
      }

      <div class="actions">
        <button mat-button (click)="step = 2"><mat-icon>arrow_back</mat-icon>Atrás</button>
        <button mat-raised-button color="accent" (click)="saveStep3(true)">Guardar y continuar<mat-icon iconPositionEnd>arrow_forward</mat-icon></button>
      </div>
    </div>
  }

  <!-- ═══ Paso 5: PDF (vale de material) ═══════════════════════════════ -->
  @if (step === 4) {
    <div class="panel">
      <p class="hint">El PDF (vale de material) lleva el folio <b>{{ record?.fileNumber }}</b>, un QR con ese folio (para identificar la foto del documento firmado), una línea por pieza con casilla de consumo, y las firmas de técnico y médico.</p>

      <div class="summary">
        <p><b>{{ pieces.length }}</b> piezas ({{ coveredPieces.length }} en CIR, {{ outPieces.length }} fuera de CIR)
          @if (otherItems.length > 0) { <span> · {{ otherItems.length }} renglones en Otros</span> }
        </p>
      </div>

      <div class="actions actions--center">
        <button mat-raised-button color="primary" (click)="generateVale()"><mat-icon>picture_as_pdf</mat-icon>Generar PDF</button>
        <button mat-raised-button color="accent" (click)="finishWizard()"><mat-icon>check_circle</mat-icon>Completar folio</button>
      </div>
      @if (valeGenerated) { <p class="count">PDF generado. Al completar, el folio pasa a <b>Pendiente</b> y sale del modo borrador.</p> }

      <div class="actions">
        <button mat-button (click)="step = 3"><mat-icon>arrow_back</mat-icon>Atrás</button>
        @if (record?.status === 'Pendiente') {
          <button mat-raised-button color="accent" (click)="step = 5">Ir a firma<mat-icon iconPositionEnd>arrow_forward</mat-icon></button>
        }
      </div>
    </div>
  }

  <!-- ═══ Paso 6: Firma (vale firmado → consumo) ═══════════════════════ -->
  @if (step === 5) {
    <div class="panel">
      @if (isSigned) {
        <p class="hint">El folio <b>{{ record?.fileNumber }}</b> ya está <b>{{ record?.status }}</b>: sus piezas quedaron como <b>Consumido</b>. Puedes ver o reemplazar el vale firmado.</p>
      } @else if (record?.status !== 'Pendiente') {
        <p class="hint hint--warn">Primero completa el folio en el paso <b>PDF</b> ("Completar folio"); después se carga aquí el vale firmado.</p>
      } @else {
        <p class="hint">Carga la <b>foto o PDF del vale firmado</b> por el técnico y el médico. Al firmar, las <b>{{ pieces.length }}</b> piezas del folio pasan a <b>Consumido</b> (quedan en la bitácora de Movimiento de insumos) y el folio pasa a <b>Firmado</b>.</p>
      }

      <div class="signed-box">
        <mat-icon>{{ signedKey ? 'task' : 'upload_file' }}</mat-icon>
        <div class="signed-box__text">
          <b>{{ signedKey ? 'Vale firmado cargado' : 'Sin vale firmado' }}</b>
          @if (signedKey) { <span>{{ signedKey.split('/').pop() }}</span> }
        </div>
        <button mat-stroked-button color="primary" [disabled]="!record || record?.status === 'Incompleto'" (click)="openSignedFile()">
          <mat-icon>{{ signedKey ? 'find_replace' : 'upload' }}</mat-icon>{{ signedKey ? 'Ver / reemplazar' : 'Cargar vale firmado' }}
        </button>
      </div>

      <div class="actions">
        <button mat-button (click)="step = 4"><mat-icon>arrow_back</mat-icon>Atrás</button>
        @if (!isSigned) {
          <button mat-raised-button color="accent" [disabled]="!signedKey || record?.status !== 'Pendiente'" (click)="signFolio()">
            <mat-icon>verified</mat-icon>Firmar y cerrar folio
          </button>
        }
      </div>
    </div>
  }
</div>
